/**
 * Sidebar.js - Fixed Enterprise Navigation Panel
 *
 * Merges application navigation and history into a permanent sidebar.
 * Layout: Fixed 260px width, full height.
 */

import { useState, useEffect, useMemo } from 'react';
import {
    SquarePen,
    Search,
    Activity,
    LayoutGrid,
    Compass,
    MoreHorizontal,
    LogOut,
    User as UserIcon,
    Zap,
    Network,
    Database,
    GitPullRequest,
    Box,
    MessageSquare
} from 'lucide-react';
import { cn } from './lib/utils';
import historyService from './services/historyService';

// Generic Sidebar Item Component
function SidebarItem({ icon: Icon, label, onClick, isActive, className, rightElement }) {
    return (
        <div
            onClick={onClick}
            className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors group select-none mx-2",
                "hover:bg-[#ececec]", // Light gray hover
                isActive ? "bg-[#ececec] text-foreground font-medium" : "text-[#0d0d0d]",
                className
            )}
        >
            <Icon className={cn("w-4 h-4 text-[#0d0d0d]", isActive && "text-primary")} strokeWidth={2} />
            <span className="text-[13px] leading-none mt-0.5 flex-1 truncate">{label}</span>
            {rightElement}
        </div>
    );
}

// Group items by time period
function groupHistoryItems(items) {
    const groups = {
        'Today': [],
        'Yesterday': [],
        'Previous 7 Days': [],
        'Older': []
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    items.forEach(item => {
        const itemDate = new Date(item.createdAt || item.created_at);
        const date = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());

        if (date.getTime() === today.getTime()) {
            groups['Today'].push(item);
        } else if (date.getTime() === yesterday.getTime()) {
            groups['Yesterday'].push(item);
        } else if (date > lastWeek) {
            groups['Previous 7 Days'].push(item);
        } else {
            groups['Older'].push(item);
        }
    });

    return groups;
}

export default function Sidebar({
    activeView,
    onNavigate,
    user,
    onLogout,
    onRestoreSearch,
    onNavigateToInvestigation
}) {
    const [historyItems, setHistoryItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSearching, setIsSearching] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Load history
    const loadHistory = async () => {
        try {
            setLoading(true);
            const { items } = await historyService.listHistory({ limit: 100 }); // Increased limit for search
            const formattedItems = items.map(historyService.formatHistoryItem);
            setHistoryItems(formattedItems);
        } catch (err) {
            console.error('Failed to load history:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadHistory();
        // Set up a listener for history updates if possible, or poll occasionally
        const interval = setInterval(loadHistory, 30000);
        return () => clearInterval(interval);
    }, []);

    // Filter and group
    const groupedItems = useMemo(() => {
        let items = historyItems;
        if (isSearching && searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            items = items.filter(item =>
                (item.displayTitle && item.displayTitle.toLowerCase().includes(q)) ||
                (item.title && item.title.toLowerCase().includes(q))
            );
        }
        return groupHistoryItems(items);
    }, [historyItems, isSearching, searchQuery]);

    const renderHistoryGroup = (title, items) => {
        if (items.length === 0) return null;

        return (
            <div className="mb-4">
                <h3 className="px-5 text-[11px] font-medium text-[#6b6b6b] mb-1 select-none">
                    {title}
                </h3>
                <div className="space-y-0.5">
                    {items.map((item) => (
                        <div
                            key={item.id}
                            onClick={() => {
                                if (item._type === 'investigation') {
                                    onNavigateToInvestigation && onNavigateToInvestigation(item.id, item);
                                } else {
                                    onRestoreSearch && onRestoreSearch(item);
                                }
                            }}
                            className="group relative px-5 py-2 text-[13px] text-[#0d0d0d] hover:bg-[#ececec] cursor-pointer transition-colors flex items-center min-h-[32px] mx-2 rounded-lg"
                        >
                            <div className="flex-1 truncate relative">
                                {item.displayTitle}
                                <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-[#f9f9f9] group-hover:from-[#ececec] to-transparent pointer-events-none" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="w-[260px] h-screen bg-[#f9f9f9] border-r border-[#e5e5e5] flex flex-col shrink-0 font-sans">

            {/* Top Section */}
            <div className="p-3 pb-0">
                {/* Logo / Brand Area could go here if needed, but keeping it clean like the reference */}

                <div className="space-y-1 mb-6">
                    <SidebarItem
                        icon={SquarePen}
                        label="New chat"
                        onClick={() => onNavigate('search')}
                        isActive={activeView === 'search'}
                    />
                    {isSearching ? (
                        <div className="flex items-center gap-2 px-3 py-1.5 mx-2 bg-white border border-blue-500/30 rounded-lg shadow-sm">
                            <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            <input
                                autoFocus
                                type="text"
                                placeholder="Search chats..."
                                className="flex-1 min-w-0 text-[13px] outline-none bg-transparent placeholder:text-muted-foreground/70"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onBlur={() => !searchQuery && setIsSearching(false)}
                                onKeyDown={(e) => e.key === 'Escape' && setIsSearching(false)}
                            />
                            <button
                                onClick={() => {
                                    setIsSearching(false);
                                    setSearchQuery('');
                                }}
                                className="text-muted-foreground hover:text-foreground"
                            >
                                <span className="sr-only">Close</span>
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                    ) : (
                        <SidebarItem
                            icon={Search}
                            label="Search"
                            onClick={() => setIsSearching(true)}
                        />
                    )}
                </div>

                <div className="space-y-1 mb-6">
                    <SidebarItem
                        icon={Activity}
                        label="Data Readiness Check"
                        isActive={activeView === 'oncall'}
                        onClick={() => onNavigate('oncall')}
                    />

                    <SidebarItem
                        icon={Network}
                        label="Data Graph"
                        isActive={activeView === 'code'}
                        onClick={() => onNavigate('code')}
                    />
                    <SidebarItem
                        icon={Zap}
                        label="Data Sync"
                        isActive={activeView === 'sync'}
                        onClick={() => onNavigate('sync')}
                    />
                    <SidebarItem
                        icon={GitPullRequest}
                        label="Data Integration"
                        isActive={activeView === 'integrations'}
                        onClick={() => onNavigate('integrations')}
                    />
                </div>
            </div>

            {/* Scrollable History Section */}
            <div className="flex-1 overflow-y-auto custom-scrollbar pt-2">
                {historyItems.length > 0 && (
                    <>
                        {renderHistoryGroup('Today', groupedItems['Today'])}
                        {renderHistoryGroup('Yesterday', groupedItems['Yesterday'])}
                        {renderHistoryGroup('Previous 7 Days', groupedItems['Previous 7 Days'])}
                        {renderHistoryGroup('Older', groupedItems['Older'])}
                    </>
                )}
            </div>

            {/* User Footer */}
            <div className="p-3 mt-auto border-t border-[#e5e5e5]">
                <button
                    className="flex items-center gap-3 w-full p-2 hover:bg-[#ececec] rounded-lg transition-colors text-sm text-[#0d0d0d] group"
                    onClick={onLogout} // Or show menu
                >
                    <div className="w-8 h-8 rounded-full bg-primary/90 flex items-center justify-center text-white text-xs font-medium">
                        {(user?.full_name || user?.email || 'VT').substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 text-left overflow-hidden">
                        <div className="font-medium text-[13px] truncate">{user?.full_name || 'Vedaya User'}</div>
                        <div className="text-[11px] text-[#6b6b6b] truncate">{user?.email}</div>
                    </div>
                    {/* Visual indicator for logout/settings */}
                    <LogOut className="w-4 h-4 text-[#6b6b6b] opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
            </div>

        </div>
    );
}
